<template>
  <div>
    <button-upload
      :full-width="true"
      accept="application/json"
      @select-file="importSimulation"
    >
      {{ $t("actions.importSimulation") }}
    </button-upload>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { blobToString } from "@/helpers/utility/ReadFile";
import { showError } from "@core/ui/alert/alert";

import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";

import ButtonUpload from "@core/input/ButtonUpload.vue";
import type { SimulationItem } from "./SimulationItem";
import { migrateSimulation_3_5 } from "./migrationScripts/3.5.0_costs";
import { useModelStore } from "../model/model.store";

const { t } = useI18n();

async function importSimulation(file: File | null) {
  try {
    if (file === null) throw new Error("No file selected");
    
    const simulationString = await blobToString(file);
    const data = JSON.parse(simulationString);
    updateSimulationData(data);
    checkSimulationIntegrity(data);
    const newSimulation = await useSimulationStore().import(data);

    loadSimulation(newSimulation);
  } catch (_error) {
    const error = _error as Error;
    console.error(error);
    if (error instanceof SyntaxError) {
      showError(t("errors.couldNotReadFile"));
    } else {
      showError(error as Error);
    }
  }
}

function loadSimulation(simulation: { id: number }) {
  // remove selection from simulation store item, if possible.
  return useSimulationViewStore().loadSimulation({
    simulationId: simulation.id
  });
}

function checkSimulationIntegrity(simulationData: SimulationItem) {
  try {
    // check targets
    const targetIds = simulationData.targetIds.slice();
    const allTargets = useTargetStore().items;
    for (const targetId of targetIds) {
      if (!(targetId in allTargets)) {
        throw new Error(`Target with id ${targetId} not found`);
      }
    }

    const resourceIds = simulationData.resourceIds.slice();
    const allResources = useResourceStore().items;
    for (const resourceId of resourceIds) {
      if (!(resourceId in allResources)) {
        throw new Error(`Resource with id ${resourceId} not found`);
      }
    }

    const allActions = useActionStore().items;
    for (const actionId of simulationData.actionIds) {
      if (!(actionId in allActions)) {
        throw new Error(`Action with id ${actionId} not found`);
      }
    }

    const allIncidents = useIncidentStore().items;
    for (const incidentId of simulationData.incidentIds) {
      if (!(incidentId in allIncidents)) {
        throw new Error(`Incident with id ${incidentId} not found`);
      }
    }
  } catch (e) {
    console.warn(e);
    const error = new Error(t("errors.simulationNotCompatible"));
    if ("cause" in error) {
      error.cause = e;
    }
    throw error;
  }
}

/**
 * Migrate simulations from old format to new format
 */
function updateSimulationData(simulationData: SimulationItem & { measureIds?: SimulationItem["actionIds"] }) {
  if (simulationData.measureIds) {
    simulationData.actionIds = simulationData.measureIds;
    delete simulationData.measureIds;
    if (simulationData.actionPlans) {
      for (const roundIndex in simulationData.actionPlans) {
        type ActionPlan = SimulationItem["actionPlans"][number];
        const actionPlan = simulationData.actionPlans[roundIndex] as ActionPlan & { plannedMeasureIds?: ActionPlan["plannedActionIds"] };
        if (actionPlan.plannedMeasureIds) {
          actionPlan.plannedActionIds = actionPlan.plannedMeasureIds;
          delete actionPlan.plannedMeasureIds;
        }
      }
    }
    if (simulationData.rounds) {
      for (const r of simulationData.rounds) {
        type Round = SimulationItem["rounds"][number];
        const round = r as Round & { 
          measureCounts?: Round["actionCounts"],
          activatedMeasures?: Round["activatedActions"],
          preventedMeasures?: Round["preventedActions"],
          notPayableMeasures?: Round["notPayableActions"],
        };

        if (round.measureCounts) {
          round.actionCounts = round.measureCounts;
          delete round.measureCounts;
        }

        for (const t in round.impulses) {
          const type = t as "target" | "resource";
          for (const id in round.impulses[type]) {
            const impulse = round.impulses[type][id];
            for (const sourceValue of impulse.sourceValues) {
              if (sourceValue.type == "measure") sourceValue.type = "action";
            }
          }
        }

        if (round.activatedMeasures != null) {
          for (const m of round.activatedMeasures) {
            const activatedMeasure = m as Round["activatedActions"][number] & { measureId?: number };
            if (activatedMeasure.measureId != null) {
              activatedMeasure.actionId = activatedMeasure.measureId;
              delete activatedMeasure.measureId;
            }
          }
          round.activatedActions = round.activatedMeasures;
        }
        if (round.preventedMeasures != null) {
          round.preventedActions = round.preventedMeasures;
          delete round.preventedMeasures;
        }
        if (round.notPayableMeasures != null) {
          round.notPayableActions = round.notPayableMeasures;
          delete round.preventedMeasures;
        }
      }
    }
  }

  const modelState = useModelStore().simulationModelState;

  migrateSimulation_3_5(simulationData, { actions: modelState.actions });
}
</script>
